import { useMemo, useState } from "react";

import { Button, toastSuccess, useModal } from "@alpacahq/ui/old";

import {
  useGetCurrentAccountIfLiveElseTradingAccount,
  useGetOAuthConnectedClients,
  useGetOAuthTokens,
  useMutationRequest,
} from "~/v2/api/hooks";
import { type GetOAuthTokensResponse, revokeOAuthToken } from "~/v2/api/oauth";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useArrayToMap } from "~/v2/utils";
import { getPrettyDate } from "~/v2/utils/formatting";

type OAuthTokenWithClientData = GetOAuthTokensResponse & {
  logo?: string;
  clientName?: string;
  liveAccountName?: string;
  liveAccountNumber?: string | null;
  paperAccountName?: string;
  paperAccountNumber?: string | null;
};

const ConnectedApps = () => {
  const [token, setToken] = useState<OAuthTokenWithClientData>();
  // TODO (MLA): Review UX
  const { account: liveAccount } =
    useGetCurrentAccountIfLiveElseTradingAccount();
  const { liveAccounts, paperAccounts, paperAccountsNoDetail } =
    useAccountContext();

  const { tokens, refetch } = useGetOAuthTokens({
    accountId: liveAccount?.id,
    options: {
      enabled: !!liveAccount?.id,
    },
  });
  const { clients: connectedClients, refetch: connectedClientRefetch } =
    useGetOAuthConnectedClients({
      accountId: liveAccount?.id,
      options: {
        enabled: !!liveAccount?.id,
      },
    });

  const connectedClientMap = useArrayToMap(connectedClients ?? [], "client_id");
  // `connectedClients ?? []` is a new array each render, but useArrayToMap
  // memoizes on it, so connectedClientMap only changes when the query settles.
  const tokensWithClientData: OAuthTokenWithClientData[] = useMemo(() => {
    if (!tokens?.length) {
      return [];
    }

    return tokens
      .filter((token) => token.status !== "REVOKED")
      .map((token) => {
        const clientData = connectedClientMap.get(token.client_id);
        const liveAccount = liveAccounts?.find(
          (live) => live.id === token.live_account_id,
        );
        const paperAccount = paperAccounts.find(
          (paper) => paper.id === token.paper_account_id,
        );
        const paperAccountName = paperAccountsNoDetail.find(
          (paper) => paper.paper_account_id === token.paper_account_id,
        )?.name;

        return {
          ...token,
          logo: clientData?.logo,
          clientName: clientData?.name,
          liveAccountName: liveAccount?.account_name ?? undefined,
          liveAccountNumber: liveAccount?.account_number,
          paperAccountName: paperAccountName,
          paperAccountNumber: paperAccount?.account_number,
        };
      });
  }, [
    tokens,
    connectedClientMap,
    liveAccounts,
    paperAccounts,
    paperAccountsNoDetail,
  ]);

  const revokeTokenMutation = useMutationRequest<
    undefined,
    OAuthTokenWithClientData
  >((token) => revokeOAuthToken(token.id, token.client_id), {
    onSuccess: () => {
      toastSuccess(`You have successfully revoked the OAuth token.`);
      refetch();
      connectedClientRefetch();
      toggle();
      setToken(undefined);
    },
  });

  const onRevokeTokenClick = () => {
    if (!token) {
      return;
    }
    revokeTokenMutation.mutate(token);
  };

  const { component: Modal, toggle } = useModal({
    size: "sm",
    title: `Revoke ${token?.clientName ? `${token.clientName} ` : ""}Token`,
    controls: (
      <div className="flex gap-2">
        <Button variant="alternate" onClick={() => toggle()}>
          Close
        </Button>

        <Button onClick={onRevokeTokenClick}>Revoke Token</Button>
      </div>
    ),
    content: (
      <>
        <div className="mb-2">
          Once you revoke this token the accounts {token?.liveAccountNumber}
          {token?.paperAccountNumber ? ` and ${token.paperAccountNumber}` : ""}{" "}
          will not have access to this app.
        </div>

        <div>Are you sure you want to revoke this token?</div>
      </>
    ),
    onClose: () => {
      setToken(undefined);
    },
  });

  const onModalOpenClick = (token: OAuthTokenWithClientData) => {
    setToken(token);
    toggle();
  };

  return (
    <div className="max-w-[600px] space-y-4">
      <h2 className="mb-8 text-xl">Connected Apps</h2>
      {tokensWithClientData.map((token) => (
        <div
          key={token.id}
          className="w-full space-y-3 rounded border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <img
                  src={token.logo}
                  style={{ width: "100%", maxWidth: "40px" }}
                  alt={token.clientName}
                />

                <div>{token.clientName}</div>
              </div>

              <div className="text-gray-600 text-xs">
                Granted {getPrettyDate(token.created_at)}
              </div>
            </div>

            <Button variant="alternate" onClick={() => onModalOpenClick(token)}>
              Revoke
            </Button>
          </div>

          <div>
            Live {token.liveAccountNumber}{" "}
            <span className="text-gray-400">{`${token.liveAccountName}`}</span>
          </div>

          <div>
            Paper {token.paperAccountNumber}{" "}
            <span className="text-gray-400">{token.paperAccountName}</span>
          </div>
        </div>
      ))}

      {Modal}
    </div>
  );
};

export default ConnectedApps;
